import { useEffect, useMemo, useRef, useState } from 'react';
import { useLang } from '../../lib/language';

export interface MultiOption { id: number; label: string }

/**
 * Professional multi-select dropdown: a chip-summary trigger that opens a
 * searchable checkbox panel with select-all / clear. Closes on outside click
 * or Escape. No external dependency.
 */
export function MultiSelect({
  options, value, onChange, placeholder, minWidth = 200,
}: {
  options: MultiOption[];
  value: number[];
  onChange: (ids: number[]) => void;
  placeholder?: string;
  minWidth?: number;
}): JSX.Element {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const selected = useMemo(() => new Set(value), [value]);
  const selectedOptions = useMemo(() => options.filter((o) => selected.has(o.id)), [options, selected]);
  const filtered = useMemo(
    () => options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase())),
    [options, query],
  );
  const allFilteredSelected = filtered.length > 0 && filtered.every((o) => selected.has(o.id));

  function toggle(id: number): void {
    onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]);
  }
  function toggleAllFiltered(): void {
    const ids = filtered.map((o) => o.id);
    if (allFilteredSelected) onChange(value.filter((v) => !ids.includes(v)));
    else onChange([...new Set([...value, ...ids])]);
  }

  return (
    <div className="ms" ref={rootRef} style={{ minWidth }}>
      <button type="button" className={`ms-trigger ${open ? 'open' : ''}`} onClick={() => setOpen((o) => !o)}>
        {value.length === 0 ? (
          <span className="ms-placeholder">{placeholder ?? t({ en: 'All', ar: 'الكل' })}</span>
        ) : (
          <span className="ms-chips">
            {selectedOptions.slice(0, 2).map((o) => (
              <span className="ms-chip" key={o.id}>
                {o.label}
                <span
                  className="ms-chip-x"
                  role="button"
                  aria-label={`${t({ en: 'Remove', ar: 'إزالة' })} ${o.label}`}
                  onClick={(e) => { e.stopPropagation(); toggle(o.id); }}
                >×</span>
              </span>
            ))}
            {value.length > 2 && <span className="ms-more">+{value.length - 2}</span>}
          </span>
        )}
        <svg className="ms-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div className="ms-panel">
          <input
            className="ms-search"
            placeholder={t({ en: 'Search…', ar: 'بحث…' })}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className="ms-panel-actions">
            <button type="button" className="ms-link" onClick={toggleAllFiltered} disabled={filtered.length === 0}>
              {allFilteredSelected ? t({ en: 'Clear shown', ar: 'مسح المعروض' }) : t({ en: 'Select all', ar: 'تحديد الكل' })}
            </button>
            {value.length > 0 && (
              <button type="button" className="ms-link" onClick={() => onChange([])}>{t({ en: 'Clear all', ar: 'مسح الكل' })} ({value.length})</button>
            )}
          </div>
          <div className="ms-list">
            {filtered.length === 0 && <div className="ms-empty">{t({ en: 'No matches', ar: 'لا توجد نتائج' })}</div>}
            {filtered.map((o) => (
              <label className="ms-opt" key={o.id}>
                <input type="checkbox" checked={selected.has(o.id)} onChange={() => toggle(o.id)} />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
